import React from "react";
import { Link } from "react-router-dom";
import MarketingNav from "../../components/MarketingNav";
import Footer from "../../components/Footer";
import SEO from "../../components/SEO";
import DigestSignup from "../../components/DigestSignup";
import { posts } from "../../content/blog";
import { Calendar, Clock, ArrowRight, Rss } from "lucide-react";

export default function Blog() {
  // Build Blog JSON-LD so Google treats this as a publication
  const schema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "Sentry Blog",
    url: "https://lunaroccultation.com/blog",
    description: "Deep technical writing on AI governance, LLM security, and audit-grade evidence from the Sentry team.",
    blogPost: posts.map((p) => ({
      "@type": "BlogPosting",
      headline: p.title,
      datePublished: p.date,
      author: { "@type": "Person", name: p.author },
      url: `https://lunaroccultation.com/blog/${p.slug}`,
    })),
  };

  const [featured, ...rest] = posts;

  return (
    <div>
      <SEO
        path="/blog"
        title="Blog · AI Governance, LLM Security, Audit Evidence"
        description="Deep technical writing on AI governance from the Sentry team. EU AI Act how-tos, prompt-injection regex, signed-evidence primers, and more."
        keywords="AI governance blog, LLM security blog, EU AI Act, prompt injection, signed evidence, LLM audit trail"
        schema={schema}
      />
      <MarketingNav />

      <section className="container-x py-20">
        <div className="max-w-3xl flex items-start justify-between gap-6 flex-wrap">
          <div>
            <span className="chip chip-orange mb-6"><Rss size={12} /> The Lunar Occultation Blog</span>
            <h1 className="font-display text-4xl md:text-5xl font-bold text-navy leading-tight">
              Field notes from the frontier of regulated AI.
            </h1>
            <p className="ink mt-5 text-lg max-w-2xl">
              Audit checklists, threat patterns, architecture decisions. Written by engineers who ship AI governance in production, not by content marketers.
            </p>
          </div>
          <a
            href="/rss.xml"
            data-testid="blog-rss-link"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-mono font-semibold bg-[#FF6B35]/10 text-[#D14F1F] hover:bg-[#FF6B35]/15 border border-[#FF6B35]/30 whitespace-nowrap mt-2"
            title="Subscribe to the RSS feed"
          >
            <Rss size={12} /> RSS
          </a>
        </div>

        {/* Featured post */}
        <Link
          to={`/blog/${featured.slug}`}
          data-testid={`blog-featured-${featured.slug}`}
          className="mt-14 block rounded-3xl overflow-hidden group bg-navy text-cream relative"
        >
          <div className="absolute inset-0 opacity-70 starfield-soft pointer-events-none" />
          <div className="absolute -right-24 -top-24 w-[420px] h-[420px] rounded-full pointer-events-none"
               style={{ background: 'radial-gradient(circle, rgba(255,107,53,0.35) 0%, transparent 60%)', filter: 'blur(20px)' }} />
          <div className="relative grid md:grid-cols-5 gap-6 p-10">
            <div className="md:col-span-3">
              <div className="eyebrow text-[#FFD98A] mb-3">Featured · Latest</div>
              <h2 className="font-display text-3xl md:text-4xl font-bold leading-tight group-hover:text-orange transition">{featured.title}</h2>
              <p className="text-cream/75 mt-4 text-base max-w-2xl">{featured.excerpt}</p>
              <div className="mt-6 flex items-center gap-4 text-xs text-cream/60">
                <span className="inline-flex items-center gap-1"><Calendar size={12} /> {formatDate(featured.date)}</span>
                <span className="inline-flex items-center gap-1"><Clock size={12} /> {featured.read_minutes} min read</span>
                <span>· {featured.author}</span>
              </div>
              <div className="mt-6 inline-flex items-center gap-1.5 text-orange font-semibold text-sm">
                Read the post <ArrowRight size={14} className="group-hover:translate-x-1 transition" />
              </div>
            </div>
            <div className="md:col-span-2 flex items-center justify-center">
              <PostArt tag={featured.tags[0]} />
            </div>
          </div>
        </Link>

        {/* Rest */}
        {rest.length > 0 && (
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {rest.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                data-testid={`blog-card-${p.slug}`}
                className="group rounded-2xl bg-white border border-[rgba(10,14,26,0.08)] p-7 hover:border-[#FF6B35] transition lift"
              >
                <div className="flex items-center gap-2 flex-wrap mb-4">
                  {p.tags.map((t) => (
                    <span key={t} className="chip chip-navy text-[10px]">{t}</span>
                  ))}
                </div>
                <h3 className="font-display text-xl font-bold text-navy group-hover:text-[#FF6B35] transition">{p.title}</h3>
                <p className="ink text-sm mt-3 leading-relaxed">{p.excerpt}</p>
                <div className="mt-5 flex items-center gap-4 text-xs ink">
                  <span className="inline-flex items-center gap-1"><Calendar size={12} /> {formatDate(p.date)}</span>
                  <span className="inline-flex items-center gap-1"><Clock size={12} /> {p.read_minutes} min</span>
                </div>
                <div className="mt-4 inline-flex items-center gap-1.5 text-[#FF6B35] text-sm font-semibold">
                  Read <ArrowRight size={13} className="group-hover:translate-x-1 transition" />
                </div>
              </Link>
            ))}
          </div>
        )}

        <div className="mt-16 max-w-4xl mx-auto">
          <DigestSignup source="/blog" />
        </div>
      </section>

      <Footer />
    </div>
  );
}

function formatDate(iso) {
  return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function PostArt({ tag }) {
  const label = tag.toUpperCase();
  return (
    <div className="relative w-[280px] h-[200px] rounded-2xl bg-white/5 border border-white/10 overflow-hidden flex items-center justify-center">
      <div className="absolute inset-0 opacity-60"
           style={{ backgroundImage: "radial-gradient(circle at 30% 20%, rgba(255,107,53,0.3), transparent 60%)" }} />
      <div className="relative text-center">
        <div className="font-mono text-[10px] text-[#FFD98A] tracking-widest mb-2">// SENTRY INTEL</div>
        <div className="font-display font-bold text-cream text-xl">{label}</div>
      </div>
    </div>
  );
}
